'use client';

import React from 'react';
import { ShieldCheck, AlertTriangle, XCircle, RefreshCw, LayoutDashboard, List, Bell } from 'lucide-react';
import type { CredentialsView } from '../page';
import Icon from '@/components/ui/AppIcon';


interface CredentialsHeaderProps {
  view: CredentialsView;
  onViewChange: (v: CredentialsView) => void;
  totalExpired: number;
  totalExpiringSoon: number;
}

const tabs: { id: CredentialsView; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'Compliance Dashboard', icon: LayoutDashboard },
  { id: 'inventory', label: 'License Inventory', icon: List },
  { id: 'alerts', label: 'Renewal Alerts', icon: Bell },
];

export default function CredentialsHeader({ view, onViewChange, totalExpired, totalExpiringSoon }: CredentialsHeaderProps) {
  return (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <ShieldCheck size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-700 text-foreground">Credential Tracking</h1>
            <p className="text-sm text-muted-foreground">License inventory, compliance status &amp; renewal management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {totalExpired > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger/10 border border-danger/20">
              <XCircle size={14} className="text-danger" />
              <span className="text-xs font-600 text-danger">{totalExpired} Expired</span>
            </div>
          )}
          {totalExpiringSoon > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-warning/10 border border-warning/20">
              <AlertTriangle size={14} className="text-warning" />
              <span className="text-xs font-600 text-warning">{totalExpiringSoon} Expiring Soon</span>
            </div>
          )}
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-600 hover:opacity-90 transition-opacity">
            <RefreshCw size={14} />
            <span>Send Reminders</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = view === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onViewChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all ${
                active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
